import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Cloud, Mail, Github, Monitor, Check, AlertCircle, Loader } from 'lucide-react';
import securityApi from '../../api/securityApi';
import toast from 'react-hot-toast';
import { MANUAL_EVIDENCE_AREAS } from '../../constants/manualEvidenceAreas';
import ManualEvidenceAreaCard from '../../components/compliance/ManualEvidenceAreaCard';

const AUTOMATED_INTEGRATIONS_CONFIG = [
  {
    type: 'aws',
    name: 'Amazon Web Services',
    description: 'IAM policies, S3 bucket security, and infrastructure configuration',
    icon: Cloud,
    iconBg: 'bg-orange-500',
  },
  {
    type: 'google_workspace',
    name: 'Google Workspace',
    description: 'User access controls, admin settings, and workspace security',
    icon: Mail,
    iconBg: 'bg-blue-500',
  },
  {
    type: 'github',
    name: 'GitHub',
    description: 'Repository security, branch protection, and code scanning settings',
    icon: Github,
    iconBg: 'bg-gray-900',
  },
  {
    type: 'gcp',
    name: 'Google Cloud Platform',
    description: 'Cloud IAM, storage security, and GCP resource configuration',
    icon: Monitor,
    iconBg: 'bg-blue-600',
    comingSoon: true,
  },
];

const IntegrationsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [integrationsData, dashboard] = await Promise.all([
          securityApi.getProjectIntegrations(projectId),
          securityApi.getDashboard(projectId),
        ]);
        setIntegrations(integrationsData.integrations || integrationsData || []);
        setDashboardData(dashboard);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load integrations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const getConnectionStatus = (type) => {
    const match = integrations.find(
      (i) => i.integration_type === type || i.type === type
    );
    if (match) {
      return { connected: true, name: match.name || match.integration_name };
    }
    return { connected: false, name: null };
  };

  // Calculate task counts for each manual evidence area
  const getManualAreaCounts = (area) => {
    if (!dashboardData?.by_control_category) {
      return { totalTasks: 0, completedTasks: 0 };
    }

    let totalTasks = 0;
    let completedTasks = 0;

    // Iterate through all control categories in dashboard data
    Object.entries(dashboardData.by_control_category).forEach(([categoryKey, categoryData]) => {
      // Check if this category matches any of the area's control prefixes
      const matchesPrefix = area.controlPrefixes.some((prefix) =>
        categoryKey.startsWith(prefix)
      );

      if (matchesPrefix) {
        totalTasks += categoryData.total || 0;
        completedTasks += categoryData.completed || 0;
      }
    });

    return { totalTasks, completedTasks };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Integrations & Evidence</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage automated integrations and manual evidence collection for compliance verification.
        </p>
      </div>

      {/* Section 1: Automated Integrations */}
      <div className="mb-8">
        <div className="mb-4 pb-2 border-b-2 border-green-500">
          <h2 className="text-lg font-semibold text-gray-900">Automated Integrations</h2>
          <p className="text-sm text-gray-600 mt-1">
            Cloud providers and services that automatically verify compliance controls.
          </p>
        </div>

        <div className="space-y-4">
          {AUTOMATED_INTEGRATIONS_CONFIG.map((config) => {
            const Icon = config.icon;
            const status = getConnectionStatus(config.type);

            return (
              <div
                key={config.type}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 ${config.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {config.name}
                        </h3>
                        {config.comingSoon && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {status.connected ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-sm text-green-700 font-medium">Connected</span>
                            <Check className="w-4 h-4 text-green-500" />
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 rounded-full bg-gray-400" />
                            <span className="text-sm text-gray-500">Not connected</span>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{config.description}</p>
                    {status.connected && status.name && (
                      <p className="text-xs text-gray-400 mt-2">
                        Integration: {status.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-600">
                To connect or modify integrations, use the onboarding wizard when creating a new compliance project
                or contact your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Areas Requiring Manual Evidence */}
      <div>
        <div className="mb-4 pb-2 border-b-2 border-blue-500">
          <h2 className="text-lg font-semibold text-gray-900">Areas Requiring Manual Evidence</h2>
          <p className="text-sm text-gray-600 mt-1">
            Compliance areas that require human documentation and cannot be automatically verified.
          </p>
        </div>

        <div className="space-y-4">
          {MANUAL_EVIDENCE_AREAS.map((area) => {
            const { totalTasks, completedTasks } = getManualAreaCounts(area);
            return (
              <ManualEvidenceAreaCard
                key={area.key}
                area={area}
                totalTasks={totalTasks}
                completedTasks={completedTasks}
                onUploadClick={() => navigate(`/compliance/projects/${projectId}?tab=tasks`)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
